import React, { ReactElement } from 'react';
import {
  ParentSidebarNavItem,
  TabNames,
} from '../../../types';
import SidebarCollapsibleHeader from './SidebarCollapsibleHeader';
import SidebarItemsPopper from './SidebarItemsPopper';
import SidebarNavItemRenderer from './SidebarNavItemRenderer';
import { useAppContext } from '../../application/app-context/AppContext';
import useSidebarState from './useSidebarState';
import useSidebarPopper from './useSidebarPopper';
import classNames from '../../../utils/class-names';
import ReadinessTag from '../halda-design-system/ReadinessTag';
import iconMap from '../../../utils/icon-map';

interface SidebarItemsProps {
  navItems: Array<ParentSidebarNavItem>;
  collapse: () => void;
  expand: () => void;
  isInitiallyExpanded: boolean;
}

const SidebarItems = ({
  navItems,
  collapse,
  expand,
  isInitiallyExpanded,
}: SidebarItemsProps): ReactElement => {
  const { currentUser, formAuthenticityToken } = useAppContext();
  const hasAdminAccess = navItems.some(
    (item) => item.name === TabNames.ALL_ACCOUNTS
  );

  const {
    isCollapsed,
    isCollapsing,
    isClickingArrow,
    arrowRef,
    expandedItems,
    startCollapse,
    startExpand,
    toggleItem
  } = useSidebarState({
    navItems,
    isInitiallyExpanded,
    currentUser,
    formAuthenticityToken
  });

  const {
    anchorEl,
    hoveredItem,
    popperHoveredRef,
    handleMouseEnter,
    handleMouseLeave
  } = useSidebarPopper(isCollapsed);

  return (
    <div className="flex-1 flex flex-col min-h-0 h-full overflow-hidden">
      <div
        className={classNames(
          'flex overflow-hidden items-center h-16 shrink-0 px-4 transform justify-start',
          isCollapsing ? 'translate-x-0' : 'translate-x-6',
          'ease-in-out duration-300'
        )}
      >
        <a href="/">
          <img
            className="h-8 w-auto"
            src={
              isCollapsed
                ? '/logos/halda_logo_white_h_only.svg'
                : '/logos/halda_logo_white.svg'
            }
            alt="Workflow"
          />
        </a>
      </div>
      <div className="flex-1 flex flex-col overflow-y-auto">
        <nav className="flex-1 px-2 py-4 space-y-1">
          <div className="pl-1">
            <SidebarCollapsibleHeader
              isCollapsed={isCollapsed}
              isCollapsing={isCollapsing}
              isClickingArrow={isClickingArrow}
              hasAdminAccess={hasAdminAccess}
              arrowRef={arrowRef}
              navItems={navItems}
              startCollapse={() => startCollapse(collapse)}
              startExpand={() => startExpand(expand)}
            />
            {navItems.map((item) => (
              <div key={`side-navbar-${item.name}`}>
                <div className="relative items-center justify-between">
                  <SidebarNavItemRenderer
                    item={item}
                    isCollapsed={isCollapsed}
                    toggleItem={toggleItem}
                    handleMouseEnter={handleMouseEnter}
                    handleMouseLeave={handleMouseLeave}
                    expandedItems={expandedItems}
                  />
                </div>
                {item.subItems && expandedItems[String(item.name)] && !isCollapsed && (
                  <>
                    {item.subItems.map((subItem) => (
                      <div className="relative" key={subItem.id}>
                        <div
                          className={classNames(
                            subItem.current ? 'bg-blue-500' : 'bg-slate-300',
                            'absolute inset-y-0 left-5 w-0.5 z-10'
                          )}
                        ></div>
                        <div className="relative z-0">
                          <a
                            id={subItem.id}
                            key={subItem.name}
                            href={subItem.href}
                            className={classNames(
                              subItem.current
                                ? 'bg-slate-700 text-white font-medium'
                                : 'text-slate-300 hover:bg-slate-900 hover:text-slate-200',
                              'nav-item group flex items-center justify-between w-full px-2 py-2 text-sm font-normal rounded-md whitespace-nowrap pl-11'
                            )}
                          >
                            <div className="flex items-center">
                              {subItem.icon && (
                                iconMap[subItem.icon] && (
                                  React.createElement(iconMap[subItem.icon], {
                                    className: classNames(
                                      subItem.current
                                        ? 'text-blue-400'
                                        : 'text-slate-400 group-hover:text-slate-400',
                                      'mr-3 shrink-0 h-5 w-5'
                                    ),
                                    'aria-hidden': true,
                                  })
                                )
                              )}
                              {subItem.name}
                            </div>
                            <ReadinessTag tag={subItem.tag} invertColors={true} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
        </nav>
      </div>
      <SidebarItemsPopper
        isCollapsed={isCollapsed}
        anchorEl={anchorEl}
        hoveredItem={hoveredItem}
        onClose={() => {
          popperHoveredRef.current = false;
          handleMouseLeave();
        }}
        onMouseEnter={() => {
          popperHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          popperHoveredRef.current = false;
          handleMouseLeave();
        }}
      />
    </div>
  );
};

export default SidebarItems;
